<template>
  <ViewWrapper headingText="HPP Link Management">
    <v-container fluid grid-list-xl>
    <v-layout row wrap align-center class="mb-4">
      <v-flex>
        <v-layout align-center>
          <ExampleHPPLinkSite></ExampleHPPLinkSite>
        </v-layout>
      </v-flex>
    </v-layout>

    <v-divider class="mb-4"></v-divider>

    <v-container>
    <v-card>
      <v-card-title>
        <h3 class="title font-weight-bold">All HPP Links</h3>
        <v-spacer></v-spacer>
        <v-btn outline color="primary" @click="showFiltersDialog = true" class="mr-2">
          <v-icon left small>fa-filter</v-icon>
          Filters{{ activeFilterCount > 0 ? ` (${activeFilterCount})` : '' }}
        </v-btn>
        <v-btn color="primary" @click="openCreateModal">
          <v-icon left small>fa-plus</v-icon>Create New HPP Link
        </v-btn>
      </v-card-title>
      <v-data-table
        :headers="hppTableHeaders"
        :items="hppTableItems"
        :loading="hppTokensLoading"
        item-key="token"
      >
        <template v-slot:items="props">
          <td>{{ props.item.account_name }}</td>
          <td>{{ props.item.terminalDisplay }}</td>
          <td class="generated-url">
            <v-layout align-center>
              <v-flex>{{ props.item.url }}</v-flex>
              <v-flex shrink>
                <v-btn icon flat small @click="copyText(props.item.url)" title="Copy Link">
                  <v-icon small>fa-copy</v-icon>
                </v-btn>
              </v-flex>
            </v-layout>
          </td>
          <td>
            <v-chip small :color="props.item.is_active ? 'success' : 'default'" :text-color="props.item.is_active ? 'white' : ''">
              {{ props.item.is_active ? 'Active' : 'Inactive' }}
            </v-chip>
          </td>
          <td>
            <v-btn flat small @click="loadTokenForEdit(props.item)">
              <v-icon small left>fa-edit</v-icon>Edit
            </v-btn>
            <v-btn v-if="props.item.is_active" flat small @click="deactivateToken(props.item)" color="error">
              <v-icon small left>fa-ban</v-icon>Deactivate
            </v-btn>
            <v-btn v-else flat small @click="reactivateToken(props.item)" color="success">
              <v-icon small left>fa-check-circle</v-icon>Re-enable
            </v-btn>
          </td>
        </template>
      </v-data-table>
    </v-card>
    </v-container>

    <!-- Create / Edit HPP Link Modal -->
    <v-dialog v-model="showHppModal" max-width="1100" scrollable>
      <v-card>
        <v-card-title class="headline primary white--text">
          {{ editingToken ? 'Edit HPP Link' : 'Create HPP Link' }}
          <v-spacer></v-spacer>
          <v-btn icon flat dark @click="showHppModal = false">
            <v-icon>fa-times</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert ref="updateSuccessAlert" v-show="updateSuccess" type="success" class="mb-3" tabindex="-1">
            HPP link updated successfully.
          </v-alert>
          <v-form ref="hppForm" v-model="hppFormValid">
            <v-layout row wrap>
              <v-flex xs12>
                <v-autocomplete
                  v-model="hppSelectedAccountId"
                  :items="myProfile.accounts"
                  :item-text="a => a.dba || a.name"
                  item-value="account_id"
                  :loading="hppLoadingTerminals"
                  @change="onHppAccountChange"
                  :clearable="!editingToken"
                  :readonly="!!editingToken"
                  :class="{ 'field-readonly-disabled': !!editingToken }"
                >
                  <template v-slot:label>Select Account <span v-if="!editingToken" class="red--text">*</span></template>
                  <template slot="item" slot-scope="{ item }">
                    <v-list-tile-content>
                      <v-list-tile-title>{{ item.dba || item.name }}</v-list-tile-title>
                      <v-list-tile-sub-title v-if="item.mid">MID: {{ item.mid }}</v-list-tile-sub-title>
                    </v-list-tile-content>
                  </template>
                </v-autocomplete>
              </v-flex>

              <template v-if="hppSelectedAccountId">
                <v-flex xs12>
                  <v-checkbox
                    v-if="showCheckbox"
                    v-model="useTwoTerminals"
                    color="primary"
                    label="Use Separate Terminals"
                    hide-details
                    :disabled="!!editingToken"
                  ></v-checkbox>
                  <div v-if="showCheckbox && useTwoTerminals" class="caption grey--text mb-2 mt-1">At least one terminal must be selected</div>
                  <v-autocomplete
                    v-if="!useTwoTerminals"
                    :class="['terminal-select', { 'field-readonly-disabled': !!editingToken }]"
                    v-model="tokenValues.selectedTerminalId"
                    :items="availableTerminals"
                    item-text="name"
                    item-value="id"
                    :readonly="!!editingToken"
                  >
                    <template v-slot:label>Terminal <span v-if="!editingToken" class="red--text">*</span></template>
                  </v-autocomplete>
                  <v-autocomplete
                    v-if="useTwoTerminals && achTerminals.length"
                    :class="['double-terminal-select', { 'field-readonly-disabled': !!editingToken }]"
                    v-model="tokenValues.selectedACHTerminalId"
                    :items="achTerminals"
                    item-text="name"
                    item-value="id"
                    :clearable="!editingToken"
                    :readonly="!!editingToken"
                  >
                    <template v-slot:label>
                      ACH Terminal<template v-if="!editingToken && !tokenValues.selectedCardTerminalId"> <span class="red--text">*</span></template>
                    </template>
                  </v-autocomplete>
                  <v-autocomplete
                    v-if="useTwoTerminals && cardTerminals.length"
                    :class="['double-terminal-select', { 'field-readonly-disabled': !!editingToken }]"
                    v-model="tokenValues.selectedCardTerminalId"
                    :items="cardTerminals"
                    item-text="name"
                    item-value="id"
                    :clearable="!editingToken"
                    :readonly="!!editingToken"
                  >
                    <template v-slot:label>
                      Card Terminal<template v-if="!editingToken && !tokenValues.selectedACHTerminalId"> <span class="red--text">*</span></template>
                    </template>
                  </v-autocomplete>
                </v-flex>

                <v-flex xs12>
                  <v-divider class="my-2"></v-divider>
                  <h4 class="title font-weight-bold primary--text mt-2 mb-1">Payment Page Options</h4>
                </v-flex>

                <v-flex xs12>
                  <v-checkbox
                    v-model="restrictPaymentTypes"
                    color="primary"
                    label="Restrict Available Payment Methods"
                    hide-details
                    class="mt-0"
                  ></v-checkbox>
                </v-flex>

                <template v-if="restrictPaymentTypes">
                  <v-flex xs12 class="pl-3">
                    <v-checkbox
                      v-if="terminalSupportsCard"
                      v-model="allowCard"
                      color="primary"
                      label="Allow Card"
                      hide-details
                      class="mt-0"
                    ></v-checkbox>
                    <v-checkbox
                      v-if="terminalSupportsACH"
                      v-model="allowACH"
                      color="primary"
                      label="Allow ACH"
                      hide-details
                      class="mt-0"
                    ></v-checkbox>
                    <span v-if="!terminalSupportsCard && !terminalSupportsACH" class="caption grey--text">Select a terminal above to configure payment methods.</span>
                  </v-flex>
                </template>

                <v-flex xs12>
                  <v-divider class="my-2"></v-divider>
                  <v-checkbox
                    v-model="consentEnabled"
                    color="primary"
                    label="Require Terms & Conditions Consent"
                    hide-details
                    class="mt-0"
                  ></v-checkbox>
                </v-flex>

                <template v-if="consentEnabled">
                  <v-flex xs12 class="pl-3">
                    <v-checkbox
                      v-model="consentRequired"
                      color="primary"
                      label="Block payment until consent is accepted"
                      hide-details
                      class="mt-0"
                    ></v-checkbox>
                  </v-flex>

                  <v-flex xs12>
                    <v-text-field
                      v-model="consentLabel"
                      label="Consent Checkbox Label (HTML allowed)"
                      placeholder="I agree to the Terms and Conditions of this payment."
                    ></v-text-field>
                  </v-flex>

                  <v-flex xs12>
                    <v-checkbox
                      v-model="consentModalEnabled"
                      color="primary"
                      label="Show Terms in a Modal Dialog"
                      hide-details
                      class="mt-0"
                    ></v-checkbox>
                  </v-flex>

                  <template v-if="consentModalEnabled">
                    <v-flex xs12>
                      <v-text-field
                        v-model="consentModalTitle"
                        label="Modal Title"
                        placeholder="Terms and Conditions"
                      ></v-text-field>
                    </v-flex>

                    <v-flex xs12>
                      <v-textarea
                        v-model="consentModalContent"
                        label="Modal Content (HTML allowed)"
                        placeholder="<p>By proceeding with this payment...</p>"
                        rows="3"
                        auto-grow
                      ></v-textarea>
                    </v-flex>
                  </template>
                </template>

                <v-flex xs12>
                  <v-divider class="my-2"></v-divider>
                  <h4 class="title font-weight-bold primary--text mt-2">Hosted Payment Link</h4>
                </v-flex>

                <v-flex xs12 v-if="!generatedUrl && !generatedUrlError">
                  <span class="caption grey--text">No link generated yet.</span>
                </v-flex>

                <v-flex xs12 v-if="generatedUrl">
                  <v-layout align-center>
                    <v-flex>
                      <span class="generated-url">{{generatedUrl}}</span>
                    </v-flex>
                    <v-flex shrink>
                      <v-btn small flat @click="copyURL">
                        <v-icon small left>fa-copy</v-icon>Copy Link
                      </v-btn>
                    </v-flex>
                  </v-layout>
                </v-flex>

                <v-flex xs12 v-if="generatedUrl">
                  <div class="caption grey--text mb-1 mt-2">Page Preview</div>
                  <iframe
                    :srcdoc="hppPreviewHtml"
                    class="hpp-preview"
                    frameborder="0"
                    scrolling="auto"
                  ></iframe>
                </v-flex>

                <v-flex xs12 v-if="generatedUrlError">
                  <span class="generated-url error--text">{{generatedUrlError}}</span>
                </v-flex>
              </template>
            </v-layout>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn flat @click="showHppModal = false">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn v-if="editingToken" flat color="error" :disabled="!editingToken" @click="regenerateURL">
            <v-icon left small>fa-refresh</v-icon>Disable &amp; Regenerate Link
          </v-btn>
          <v-btn color="primary" :disabled="!hppSelectedAccountId || (!tokenValues.selectedTerminalId && !tokenValues.selectedACHTerminalId && !tokenValues.selectedCardTerminalId)" @click="editingToken ? updateURL() : getURL()">
            <v-icon left small>{{ editingToken ? 'fa-save' : 'fa-link' }}</v-icon>
            {{ editingToken ? 'Update Link' : 'Create Link' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Regenerate Success Dialog -->
    <v-dialog v-model="showRegenerateSuccessDialog" max-width="560" persistent>
      <v-card>
        <v-card-title class="headline primary white--text">
          New HPP Link Generated
          <v-spacer></v-spacer>
          <v-btn icon flat dark @click="showRegenerateSuccessDialog = false">
            <v-icon>fa-times</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <p class="body-1 mb-3">The previous link has been disabled and a new link has been generated:</p>
          <v-layout align-center>
            <v-flex>
              <span class="generated-url">{{ regeneratedUrl }}</span>
            </v-flex>
            <v-flex shrink>
              <v-btn small flat @click="copyText(regeneratedUrl)">
                <v-icon small left>fa-copy</v-icon>Copy Link
              </v-btn>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="showRegenerateSuccessDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Filters Modal -->
    <v-dialog v-model="showFiltersDialog" max-width="480">
      <v-card>
        <v-card-title class="headline primary white--text">
          Filter HPP Links
          <v-spacer></v-spacer>
          <v-btn icon flat dark @click="showFiltersDialog = false">
            <v-icon>fa-times</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-autocomplete
            v-model="pendingFilterAccountId"
            :items="myProfile.accounts"
            :item-text="a => a.dba || a.name"
            item-value="account_id"
            label="Account"
            prepend-icon="fa-building"
            box
            clearable
            hide-details
            multiple
            chips
            small-chips
            deletable-chips
            class="mb-3"
          >
            <template slot="item" slot-scope="{ item }">
              <v-list-tile-content>
                <v-list-tile-title>{{ item.dba || item.name }}</v-list-tile-title>
                <v-list-tile-sub-title v-if="item.mid">MID: {{ item.mid }}</v-list-tile-sub-title>
              </v-list-tile-content>
            </template>
          </v-autocomplete>
          <v-autocomplete
            v-model="pendingFilterTerminal"
            :items="filterAvailableTerminals"
            item-text="name"
            item-value="id"
            label="Terminal"
            prepend-icon="fa-terminal"
            box
            clearable
            hide-details
            multiple
            chips
            small-chips
            deletable-chips
            class="mb-3"
          />
          <v-text-field
            v-model="pendingFilterUrl"
            label="HPP Link (token)"
            prepend-icon="fa-link"
            box
            clearable
            hide-details
            class="mb-3"
          />
          <div class="caption grey--text mb-1 mt-2">Status</div>
          <v-radio-group v-model="pendingFilterStatus" hide-details class="mt-0">
            <v-radio label="Active only" value="active" color="primary"></v-radio>
            <v-radio label="Inactive only" value="inactive" color="primary"></v-radio>
            <v-radio label="All" value="all" color="primary"></v-radio>
          </v-radio-group>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn flat @click="clearTableFilters">Clear Filters</v-btn>
          <v-spacer></v-spacer>
          <v-btn flat @click="showFiltersDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="applyTableFilters">Done</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar bottom centered color="success" v-model="isLinkCopied">
      <v-layout row wrap align-center>
        <v-flex>
          <v-icon>fa-info-circle</v-icon>
        </v-flex>
        <v-flex subheading>
          Link copied to clipboard
        </v-flex>
      </v-layout>
    </v-snackbar>
    </v-container>
  </ViewWrapper>
</template>

<script>
import ViewWrapper from '@/components/ViewWrapper'
import ExampleHPPLinkSite from '@/components/help/ExampleHPPLinkSiteDialog'
import APIMixin from '@/mixins/api'
import AuthMixin from '@/mixins/auth'
import StoreMixin from '@/store/mixin'

export default {
  components: {
    ViewWrapper,
    ExampleHPPLinkSite
  },

  mixins: [
    APIMixin,
    AuthMixin,
    StoreMixin
  ],

  data: function () {
    return {
      availableTerminals: [],
      achTerminals: [],
      cardTerminals: [],
      useTwoTerminals: false,
      showCheckbox: false,
      restrictPaymentTypes: false,
      allowCard: true,
      allowACH: true,
      consentEnabled: false,
      consentRequired: false,
      consentLabel: '',
      consentModalEnabled: false,
      consentModalTitle: '',
      consentModalContent: '',
      tokenValues: {
        amount: undefined,
        method: undefined,
        name: undefined,
        billingName: undefined,
        email: undefined,
        phone: undefined,
        address1: undefined,
        address2: undefined,
        city: undefined,
        state: undefined,
        zip: undefined,
        customField1: undefined,
        customField2: undefined,
        customField3: undefined,
        selectedTerminalId: null,
        selectedACHTerminalId: null,
        selectedCardTerminalId: null
      },
      generatedUrl: '',
      generatedToken: null,
      editingToken: null,
      generatedUrlError: '',
      updateSuccess: false,
      hppFormValid: false,
      isLinkCopied: false,
      hppSelectedAccountId: null,
      hppLoadingTerminals: false,
      hppTokens: [],
      hppTokensLoading: false,
      showHppModal: false,
      showRegenerateSuccessDialog: false,
      regeneratedUrl: '',
      showFiltersDialog: false,
      tableFilterAccountId: [],
      tableFilterStatus: 'active',
      tableFilterTerminal: [],
      tableFilterUrl: '',
      pendingFilterAccountId: [],
      pendingFilterStatus: 'active',
      pendingFilterTerminal: [],
      pendingFilterUrl: '',
      hppTableHeaders: [
        { text: 'Account', value: 'account_name' },
        { text: 'Terminal(s)', value: 'terminalDisplay' },
        { text: 'HPP Link', value: 'url' },
        { text: 'Status', value: 'is_active', sortable: true },
        { text: 'Actions', value: '', sortable: false }
      ]
    }
  },

  watch: {
    showFiltersDialog (val) {
      if (val) {
        this.pendingFilterAccountId = this.tableFilterAccountId.slice()
        this.pendingFilterStatus = this.tableFilterStatus
        this.pendingFilterTerminal = this.tableFilterTerminal.slice()
        this.pendingFilterUrl = this.tableFilterUrl
      }
    },
    pendingFilterAccountId () {
      this.pendingFilterTerminal = []
    },
    'tokenValues.selectedTerminalId' () {
      this.generatedUrl = ''
      this.generatedToken = null
      this.editingToken = null
    },
    'tokenValues.selectedACHTerminalId' () {
      this.generatedUrl = ''
      this.generatedToken = null
      this.editingToken = null
    },
    'tokenValues.selectedCardTerminalId' () {
      this.generatedUrl = ''
      this.generatedToken = null
      this.editingToken = null
    },
  },

  mounted () {
    this.fetchHppTokens()
  },

  computed: {
    environment () {
      return process.env.NODE_ENV
    },
    hppPreviewHtml () {
      const account = (this.myProfile.accounts || []).find(a => a.account_id === this.hppSelectedAccountId)
      const accountName = account ? account.name : ''
      const imageBase = process.env.VUE_APP_IMAGE_URL || (process.env.NODE_ENV === 'development' ? 'https://paynetworx-dev-images.s3.us-west-2.amazonaws.com' : 'https://paynetworx-images.s3.us-west-2.amazonaws.com')
      const logoUrl = account && account.logo ? `${imageBase}/${account.logo}` : null

      const showCard = this.terminalSupportsCard && (!this.restrictPaymentTypes || this.allowCard)
      const showACH = this.terminalSupportsACH && (!this.restrictPaymentTypes || this.allowACH)

      const methodButtons = (() => {
        if (!showCard && !showACH) {
          return '<p style="color:#9b9b9b;font-size:13px;">No payment methods available for this terminal.</p>'
        }
        const w = showCard && showACH ? '50%' : '100%'
        const card = showCard ? `
          <button style="width:${w};padding:14px 0;background:#44a9e2;color:#fff;font-size:15px;font-weight:500;border:none;border-radius:4px;cursor:pointer;letter-spacing:.05em;">PAY BY CARD</button>` : ''
        const ach = showACH ? `
          <button style="width:${w};padding:14px 0;background:${showCard ? 'transparent' : '#44a9e2'};color:${showCard ? '#44a9e2' : '#fff'};font-size:15px;font-weight:500;border:${showCard ? '1px solid #44a9e2' : 'none'};border-radius:4px;cursor:pointer;letter-spacing:.05em;">PAY BY ACH</button>` : ''
        return `<div style="display:flex;gap:12px;margin-bottom:20px;">${card}${ach}</div>`
      })()

      const cardFields = !showCard ? '' : `
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:0 16px;margin-top:4px;">
          ${field('Card Number', true)}
          ${field('Cardholder Name', true)}
          ${field('Expiration Date (MM/YY)', true)}
          ${field('AVS/CVV', true)}
        </div>`

      const consentHtml = this.consentEnabled ? (() => {
        const label = this.consentLabel || 'I agree to the Terms and Conditions of this payment.'
        const readMore = (this.consentModalEnabled && this.consentModalTitle)
          ? ` <span style="font-size:12px;color:#44a9e2;text-decoration:underline;cursor:pointer;">Read more...</span>` : ''
        return `
          <div style="margin:16px 0 12px;">
            <label style="display:flex;align-items:flex-start;gap:8px;cursor:pointer;">
              <input type="checkbox" style="margin-top:2px;accent-color:#44a9e2;">
              <span style="font-size:13px;color:rgba(0,0,0,0.72);line-height:1.4;">${label}${readMore}</span>
            </label>
          </div>`
      })() : ''

      function field (label, required) {
        return `
          <div style="margin-bottom:16px;">
            <label style="display:block;font-size:11px;color:#9b9b9b;margin-bottom:4px;">
              ${label}${required ? ' <span style="color:#44a9e2;">*</span>' : ''}
            </label>
            <div style="border-bottom:1px solid #9b9b9b;height:24px;"></div>
          </div>`
      }

      function panel (bold, normal, open, content) {
        return `
          <div style="margin-bottom:2px;">
            <div style="display:flex;align-items:center;justify-content:space-between;background:rgba(216,216,216,0.52);height:62px;padding:0 16px;box-shadow:0 2px 4px 0 rgba(0,0,0,0.25);">
              <span style="font-size:28px;font-weight:100;color:#9b9b9b;"><strong style="font-weight:400;">${bold}</strong> ${normal}</span>
              <div style="width:41px;height:41px;border-radius:50%;background:#fff;display:flex;align-items:center;justify-content:center;">
                <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#9b9b9b" d="${open ? 'M7 14l5-5 5 5z' : 'M7 10l5 5 5-5z'}"/></svg>
              </div>
            </div>
            ${open ? `<div style="padding:20px 16px 16px;">${content}</div>` : ''}
          </div>`
      }

      return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@100;300;400;500;700;900&display=swap" rel="stylesheet">
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Roboto, sans-serif; background: #fff; color: rgba(0,0,0,0.72); }
    .hpp-app { max-width: 1080px; margin: auto; padding: 0 40px 40px; }
  </style>
</head>
<body>
  <div class="hpp-app">
    <div style="padding:16px 0 24px;display:flex;justify-content:space-between;align-items:flex-end;border-bottom:1px solid #eee;margin-bottom:24px;">
      ${logoUrl ? `<img src="${logoUrl}" alt="${accountName}" style="max-height:110px;max-width:200px;object-fit:contain;object-position:bottom left;">` : `<div style="font-size:20px;color:rgba(0,0,0,0.72);font-weight:300;">${accountName}</div>`}
      <div style="font-size:14px;color:rgba(0,0,0,0.72);font-weight:300;">Fields with <i style="color:#44a9e2;">blue asterisk*</i> are mandatory.</div>
    </div>

    <div style="display:flex;gap:40px;align-items:flex-start;">
      <div style="flex:1;min-width:0;">
        <div style="margin-bottom:20px;">
          <label style="display:block;font-size:11px;color:#9b9b9b;margin-bottom:4px;">Amount <span style="color:#44a9e2;">*</span></label>
          <div style="border-bottom:1px solid #9b9b9b;height:24px;"></div>
        </div>

        ${panel('Customer', 'Information', false, '')}
        ${panel('Payment', 'Details', true, methodButtons + cardFields)}
        ${panel('Billing', 'Information', false, '')}
      </div>

      <div style="width:220px;flex-shrink:0;">
        <div style="font-size:20px;color:rgba(0,0,0,0.85);font-weight:100;margin-bottom:24px;">Summary</div>
        <div style="font-size:14px;color:#9b9b9b;margin-bottom:6px;">Amount: <span style="color:rgba(0,0,0,0.85);">—</span></div>
        <div style="font-size:14px;color:#9b9b9b;margin-bottom:6px;">Payment: <span style="color:rgba(0,0,0,0.85);">${showCard && showACH ? 'Card / ACH' : showCard ? 'Card' : showACH ? 'ACH' : '—'}</span></div>
        ${consentHtml}
        <button style="width:100%;margin-top:16px;padding:14px;background:#83dc25;color:#fff;font-size:24px;font-weight:900;border:1px solid #fff;border-radius:2.4px;box-shadow:2px 3px 2px 0 rgba(0,0,0,0.1);cursor:pointer;">Pay Now</button>
      </div>
    </div>
  </div>
</body>
</html>`
    },

    filterAvailableTerminals () {
      const all = [...(this.terminalsCardCanCharge || []), ...(this.terminalsACHCanCharge || [])]
      const seen = new Set()
      const unique = all.filter(t => {
        if (seen.has(t.id)) return false
        seen.add(t.id)
        return true
      })
      if (this.pendingFilterAccountId && this.pendingFilterAccountId.length) {
        return unique.filter(t => this.pendingFilterAccountId.includes(t.account_id))
      }
      return unique
    },

    activeFilterCount () {
      let count = 0
      if (this.tableFilterAccountId.length) count++
      if (this.tableFilterStatus !== 'all') count++
      if (this.tableFilterTerminal.length) count++
      if (this.tableFilterUrl) count++
      return count
    },

    hppTableItems () {
      const env = process.env.NODE_ENV
      const isQa = typeof window !== 'undefined' && window.location.hostname.includes('-qa.')
      const subdomain = env === 'development' ? '-dev' : isQa ? '-qa' : ''
      let tokens = this.hppTokens
      if (this.tableFilterStatus === 'active') {
        tokens = tokens.filter(t => t.is_active)
      } else if (this.tableFilterStatus === 'inactive') {
        tokens = tokens.filter(t => !t.is_active)
      }
      if (this.tableFilterAccountId.length) {
        tokens = tokens.filter(t => this.tableFilterAccountId.includes(t.account_id))
      }
      if (this.tableFilterTerminal.length) {
        tokens = tokens.filter(t =>
          this.tableFilterTerminal.includes(t.terminal_id) ||
          this.tableFilterTerminal.includes(t.ach_terminal_id) ||
          this.tableFilterTerminal.includes(t.card_terminal_id)
        )
      }
      if (this.tableFilterUrl) {
        const term = this.tableFilterUrl.toLowerCase()
        tokens = tokens.filter(t => t.token && t.token.toLowerCase().includes(term))
      }
      return tokens.map(t => ({
        ...t,
        terminalDisplay: t.terminal_name
          ? t.terminal_name
          : [t.ach_terminal_name && `ACH: ${t.ach_terminal_name}`, t.card_terminal_name && `Card: ${t.card_terminal_name}`].filter(Boolean).join(' / '),
        url: `https://payments${subdomain}.paynetworx.com?token=${t.token}`
      }))
    },
    terminalSupportsCard () {
      if (this.useTwoTerminals) return !!this.tokenValues.selectedCardTerminalId
      if (!this.tokenValues.selectedTerminalId || !this.cardTerminals) return false
      return this.cardTerminals.some(t => t.id === this.tokenValues.selectedTerminalId)
    },
    terminalSupportsACH () {
      if (this.useTwoTerminals) return !!this.tokenValues.selectedACHTerminalId
      if (!this.tokenValues.selectedTerminalId || !this.achTerminals) return false
      return this.achTerminals.some(t => t.id === this.tokenValues.selectedTerminalId)
    }
  },

  methods: {
    copyURL () {
      this.copyText(this.generatedUrl)
    },

    copyText (text) {
      if (text && navigator && navigator.clipboard && navigator.clipboard.writeText) {
        this.isLinkCopied = true
        return navigator.clipboard.writeText(text)
      }
    },

    async fetchHppTokens () {
      this.hppTokensLoading = true
      try {
        let { data } = await this.api({ method: 'get', url: '/hosted_payment' })
        if (!data.error) this.hppTokens = data.tokens
      } catch (err) {
        console.error(err)
      } finally {
        this.hppTokensLoading = false
      }
    },

    async onHppAccountChange () {
      this.tokenValues.selectedTerminalId = null
      this.tokenValues.selectedACHTerminalId = null
      this.tokenValues.selectedCardTerminalId = null
      this.generatedUrl = ''
      this.generatedToken = null
      this.generatedUrlError = ''
      this.availableTerminals = []
      this.achTerminals = []
      this.cardTerminals = []
      this.useTwoTerminals = false
      this.showCheckbox = false

      if (!this.hppSelectedAccountId) return

      this.hppLoadingTerminals = true
      try {
        let result = await this.api({
          method: 'get',
          url: '/terminals',
          params: {
            filter: { account_id: this.hppSelectedAccountId, active: 1 },
            perPage: 999
          }
        })

        const terminals = (result.data.rows || []).filter(t => t.is_virtual === 1)

        const terminalArr = terminals.filter((v, i, a) => a.findIndex(t => t.code === v.code) === i)
        this.availableTerminals = terminalArr

        const bothTerminals = terminalArr.filter(terminal => {
          if (terminal.type && terminal.type[0] === '[') {
            return JSON.parse(terminal.type).some(item => item === 'ach') && JSON.parse(terminal.type).some(item => item !== 'ach')
          }
          return false
        })

        this.achTerminals = [...terminalArr.filter(terminal => {
          if (terminal.type === 'ach') return true
          if (terminal.type && terminal.type[0] === '[') return !JSON.parse(terminal.type).some(item => item !== 'ach')
          return false
        }), ...bothTerminals]

        this.cardTerminals = [...terminalArr.filter(terminal => {
          if (terminal.type && terminal.type[0] === '[') return !JSON.parse(terminal.type).some(item => item === 'ach')
          return terminal.type !== 'ach'
        }), ...bothTerminals]

        if (!bothTerminals.length) {
          if (this.achTerminals.length > 0 && this.cardTerminals.length > 0) {
            this.useTwoTerminals = true
            this.showCheckbox = true
          }
        } else if ((this.achTerminals.length - bothTerminals.length) > 0 || (this.cardTerminals.length - bothTerminals.length) > 0) {
          this.showCheckbox = true
        }

      } catch (err) {
        console.error(err)
      } finally {
        this.hppLoadingTerminals = false
      }
    },

    async regenerateURL () {
      if (!this.editingToken) return
      if (!confirm('Are you sure you want to disable the current link and generate a new one?')) return

      try {
        await this.api({ method: 'put', url: `/hosted_payment/${this.editingToken}/deactivate` })

        const terminalId = !this.useTwoTerminals
          ? this.tokenValues.selectedTerminalId
          : { ach: this.tokenValues.selectedACHTerminalId, card: this.tokenValues.selectedCardTerminalId }

        const { data } = await this.api({
          method: 'post',
          url: '/hosted_payment/tokenize',
          data: {
            accountId: this.hppSelectedAccountId,
            forceNew: true,
            terminalId
          }
        })

        if (!data.error) {
          const subdomain = process.env.NODE_ENV === 'development' ? '-dev' : window.location.hostname.includes('-qa.') ? '-qa' : ''
          this.regeneratedUrl = `https://payments${subdomain}.paynetworx.com?token=${data.token}`
          this.showHppModal = false
          this.showRegenerateSuccessDialog = true
          this.fetchHppTokens()
        } else {
          this.generatedUrlError = 'An error has occurred. Please relog and try again.'
        }
      } catch (err) {
        this.generatedUrlError = 'An error has occurred. Please relog and try again.'
        console.error(err)
      }
    },

    async updateURL () {
      if (!this.generatedToken) return

      this.generatedUrlError = ''

      const custom = {}

      if (this.consentEnabled) {
        custom.consent = {
          enabled: true,
          required: this.consentRequired,
          label: this.consentLabel || 'I agree to the Terms and Conditions of this payment.'
        }
        if (this.consentModalEnabled) {
          custom.consent.modal = {
            enabled: true,
            title: this.consentModalTitle || 'Terms and Conditions',
            content: this.consentModalContent || ''
          }
        }
      } else {
        custom.consent = { enabled: false }
      }

      if (this.restrictPaymentTypes) {
        const paymentTypesAllowed = []
        if (this.allowCard && this.terminalSupportsCard) paymentTypesAllowed.push('card')
        if (this.allowACH && this.terminalSupportsACH) paymentTypesAllowed.push('ach')
        custom.paymentTypesAllowed = paymentTypesAllowed.length ? paymentTypesAllowed : null
      } else {
        custom.paymentTypesAllowed = null
      }

      try {
        let { data } = await this.api({
          method: 'put',
          url: `/hosted_payment/${this.generatedToken}`,
          data: { custom }
        })

        if (!data.error) {
          this.generatedUrl = `https://payments${process.env.NODE_ENV === 'development' ? '-dev' : window.location.hostname.includes('-qa.') ? '-qa' : ''}.paynetworx.com?token=${this.generatedToken}`
          this.updateSuccess = true
          await this.fetchHppTokens()
          await this.$nextTick()
          const cardText = document.querySelector('.v-dialog--active .v-card__text')
          if (cardText) cardText.scrollTop = 0
          if (this.$refs.updateSuccessAlert) {
            this.$refs.updateSuccessAlert.$el.focus()
          }
        } else {
          this.generatedUrlError = 'An error has occurred updating the link. Please relog and try again.'
        }
      } catch (err) {
        this.generatedUrlError = 'An error has occurred updating the link. Please relog and try again.'
        console.error(err)
      }
    },

    openCreateModal () {
      this.hppSelectedAccountId = null
      this.availableTerminals = []
      this.achTerminals = []
      this.cardTerminals = []
      this.useTwoTerminals = false
      this.showCheckbox = false
      this.restrictPaymentTypes = false
      this.allowCard = true
      this.allowACH = true
      this.consentEnabled = false
      this.consentRequired = false
      this.consentLabel = ''
      this.consentModalEnabled = false
      this.consentModalTitle = ''
      this.consentModalContent = ''
      this.tokenValues = {
        amount: undefined, method: undefined, name: undefined,
        billingName: undefined, email: undefined, phone: undefined,
        address1: undefined, address2: undefined, city: undefined,
        state: undefined, zip: undefined, customField1: undefined,
        customField2: undefined, customField3: undefined,
        selectedTerminalId: null, selectedACHTerminalId: null, selectedCardTerminalId: null
      }
      this.generatedUrl = ''
      this.generatedToken = null
      this.editingToken = null
      this.generatedUrlError = ''
      this.updateSuccess = false
      this.showHppModal = true
    },

    applyTableFilters () {
      this.tableFilterAccountId = this.pendingFilterAccountId
      this.tableFilterStatus = this.pendingFilterStatus
      this.tableFilterTerminal = this.pendingFilterTerminal
      this.tableFilterUrl = this.pendingFilterUrl
      this.showFiltersDialog = false
    },

    clearTableFilters () {
      this.pendingFilterAccountId = []
      this.pendingFilterStatus = 'all'
      this.pendingFilterTerminal = []
      this.pendingFilterUrl = ''
    },

    async deactivateToken (row) {
      if (!confirm(`Deactivate this HPP link?\n${row.url}`)) return
      try {
        let { data } = await this.api({ method: 'put', url: `/hosted_payment/${row.token}/deactivate` })
        if (!data.error) {
          if (this.editingToken === row.token) {
            this.generatedUrl = ''
            this.generatedToken = null
            this.editingToken = null
          }
          this.fetchHppTokens()
        }
      } catch (err) {
        console.error(err)
      }
    },

    async reactivateToken (row) {
      if (!confirm(`Re-enable this HPP link?\n${row.url}`)) return
      try {
        let { data } = await this.api({ method: 'put', url: `/hosted_payment/${row.token}/reactivate` })
        if (!data.error) this.fetchHppTokens()
      } catch (err) {
        console.error(err)
      }
    },

    async loadTokenForEdit (row) {
      this.hppSelectedAccountId = row.account_id
      await this.onHppAccountChange()

      // Explicitly set terminal mode and selections based on the token's stored data,
      // overriding whatever onHppAccountChange may have inferred from account terminals.
      if (row.terminal_id) {
        this.useTwoTerminals = false
        this.tokenValues.selectedTerminalId = row.terminal_id
      } else {
        this.useTwoTerminals = true
        if (row.card_terminal_id) this.tokenValues.selectedCardTerminalId = row.card_terminal_id
        if (row.ach_terminal_id) this.tokenValues.selectedACHTerminalId = row.ach_terminal_id
      }

      // Parse custom settings
      let custom = {}
      try {
        custom = typeof row.custom === 'string' ? JSON.parse(row.custom) : (row.custom || {})
      } catch (e) { custom = {} }

      const consent = custom.consent || {}
      this.consentEnabled = !!consent.enabled
      this.consentRequired = !!consent.required
      this.consentLabel = consent.label || ''
      this.consentModalEnabled = !!(consent.modal && consent.modal.enabled)
      this.consentModalTitle = (consent.modal && consent.modal.title) || ''
      this.consentModalContent = (consent.modal && consent.modal.content) || ''

      if (custom.paymentTypesAllowed && Array.isArray(custom.paymentTypesAllowed)) {
        this.restrictPaymentTypes = true
        this.allowCard = custom.paymentTypesAllowed.includes('card')
        this.allowACH = custom.paymentTypesAllowed.includes('ach')
      } else {
        this.restrictPaymentTypes = false
        this.allowCard = true
        this.allowACH = true
      }

      // Wait for terminal-change watchers to flush (they clear editingToken/generatedUrl),
      // then set edit state so it persists when the modal opens.
      await this.$nextTick()

      this.editingToken = row.token
      this.generatedToken = row.token
      this.generatedUrl = row.url
      this.generatedUrlError = ''
      this.updateSuccess = false
      this.showHppModal = true
    },

    async getURL () {
      this.generatedUrl = ''
      this.generatedUrlError = ''
      this.editingToken = null

      if (this.tokenValues.selectedTerminalId || this.tokenValues.selectedCardTerminalId || this.tokenValues.selectedACHTerminalId) {
        try {
          let tokenData = {
            accountId: this.hppSelectedAccountId,
            forceNew: true,
            ...this.tokenValues,
            terminalId: !this.useTwoTerminals
              ? this.tokenValues.selectedTerminalId
              : { ach: this.tokenValues.selectedACHTerminalId, card: this.tokenValues.selectedCardTerminalId }
          }

          if (this.consentEnabled) {
            tokenData.custom = {
              consent: {
                enabled: true,
                required: this.consentRequired,
                label: this.consentLabel || 'I agree to the Terms and Conditions of this payment.'
              }
            }
            if (this.consentModalEnabled) {
              tokenData.custom.consent.modal = {
                enabled: true,
                title: this.consentModalTitle || 'Terms and Conditions',
                content: this.consentModalContent || ''
              }
            }
          }

          if (this.restrictPaymentTypes) {
            const paymentTypesAllowed = []
            if (this.allowCard && this.terminalSupportsCard) paymentTypesAllowed.push('card')
            if (this.allowACH && this.terminalSupportsACH) paymentTypesAllowed.push('ach')
            if (paymentTypesAllowed.length) {
              if (!tokenData.custom) tokenData.custom = {}
              tokenData.custom.paymentTypesAllowed = paymentTypesAllowed
            }
          }

          let { data } = await this.api({
            method: 'post',
            url: `/hosted_payment/tokenize`,
            data: tokenData
          })

          if (!data.error) {
            this.generatedToken = data.token
            this.generatedUrl = `https://payments${process.env.NODE_ENV === 'development' ? '-dev' : window.location.hostname.includes('-qa.') ? '-qa' : ''}.paynetworx.com?token=${data.token}`
            this.fetchHppTokens()
          } else {
            console.log(data)
            this.generatedUrlError = 'An error has occurred. Please relog and try again.'
          }
        } catch (err) {
          this.generatedUrlError = 'An error has occurred. Please relog and try again.'

          console.error(err)
        }
      }
    }
  }
}
</script>

<style scoped>
.field-readonly-disabled {
  opacity: 0.6;
  pointer-events: none;
}

.v-input.terminal-select {
  height: 50px;
}
.v-input.double-terminal-select {
  height: 60px;
  font-size: 14px;
}
.generated-url {
  word-break: break-all;
}
.hpp-preview {
  width: 100%;
  height: 600px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}
</style>



// WEBPACK FOOTER //
// src/components/manage/HppLinkManagement.vue